import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { GET, POST, DELETE, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

export default function DailyWork() {
	const reduxAccount = useSelector(state => state.account)
	const today = formatDate(new Date(), 'yyyy-MM-dd')

	const [allProductTypes, setAllProductTypes] = useState([])
	const [myTypes, setMyTypes] = useState([])
	const [logs, setLogs] = useState([])

	const defaultForm = () => ({
		work_date: today,
		product_type_id: '',
		workers_count: 1,
		paint_kg: '',
		note: '',
		items: [{ product_name: '', quantity: '' }],
	})
	const [form, setForm] = useState(defaultForm())

	async function loadProductTypes() {
		const res = await GET('/helper/products-types')
		if (httpOk(res)) {
			setAllProductTypes(res.data)
			const workerTypeIds = reduxAccount.product_types || []
			const filtered = res.data.filter(pt => workerTypeIds.includes(pt.id))
			setMyTypes(filtered)
			if (filtered.length === 1) {
				setForm(prev => ({ ...prev, product_type_id: filtered[0].id }))
			}
		}
	}

	async function loadMyLogs() {
		const res = await GET('/daily-work/my')
		if (httpOk(res)) setLogs(res.data)
	}

	useEffect(() => {
		loadProductTypes()
		loadMyLogs()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	function addItem() {
		setForm(prev => ({ ...prev, items: [...prev.items, { product_name: '', quantity: '' }] }))
	}

	function removeItem(idx) {
		setForm(prev => {
			const items = prev.items.filter((_, i) => i !== idx)
			return { ...prev, items: items.length ? items : [{ product_name: '', quantity: '' }] }
		})
	}

	function updateItem(idx, field, value) {
		setForm(prev => {
			const items = [...prev.items]
			items[idx] = { ...items[idx], [field]: value }
			return { ...prev, items }
		})
	}

	async function submit(e) {
		e.preventDefault()
		const res = await POST('/daily-work', form, { loader: true })
		if (httpOk(res)) {
			const newForm = defaultForm()
			if (myTypes.length === 1) newForm.product_type_id = myTypes[0].id
			setForm(newForm)
			loadMyLogs()
		}
	}

	async function deleteLog(id) {
		const res = await DELETE('/daily-work/' + id)
		if (httpOk(res)) loadMyLogs()
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-3">
				<h4 className="vertical-center mb-0">Kunlik ish hisoboti</h4>
				<span className="text-muted vertical-center">{reduxAccount.name}</span>
			</div>

			<div className="row">
				{/* FORM */}
				<div className="col-md-5">
					<div className="card">
						<div className="card-body">
							<h6 className="card-title fw-bold mb-3">Yangi yozuv</h6>
							<form onSubmit={submit}>
								<div className="form-group">
									<label>Sana <span className="required-mark">*</span></label>
									<input type="date" className="form-control"
										value={form.work_date}
										onChange={e => setForm({ ...form, work_date: e.target.value })} />
								</div>

								{myTypes.length > 1 && (
									<div className="form-group">
										<label>Bo'lim <span className="required-mark">*</span></label>
										<select className="form-control" value={form.product_type_id}
											onChange={e => setForm({ ...form, product_type_id: Number(e.target.value) })}>
											<option value="">— Tanlang —</option>
											{myTypes.map(pt => (
												<option key={pt.id} value={pt.id}>{pt.name}</option>
											))}
										</select>
									</div>
								)}

								{myTypes.length === 1 && (
									<div className="form-group">
										<label>Bo'lim</label>
										<div className="form-control bg-light">{myTypes[0].name}</div>
									</div>
								)}

								<div className="form-group">
									<label>Bugun ishlagan ishchilar soni <span className="required-mark">*</span></label>
									<input type="number" className="form-control" min="1" step="1"
										value={form.workers_count}
										onChange={e => setForm({ ...form, workers_count: Math.max(1, parseInt(e.target.value) || 1) })} />
								</div>

								<div className="form-group">
									<label>Kraska (kg)</label>
									<input type="number" className="form-control" step="0.001" min="0"
										value={form.paint_kg}
										onChange={e => setForm({ ...form, paint_kg: e.target.value })} />
								</div>

								<div className="form-group">
									<label className="d-block mb-2">Tayyorlangan mahsulotlar</label>
									{form.items.map((item, idx) => (
										<div key={idx} className="d-flex gap-2 mb-2">
											<input type="text" className="form-control" placeholder="Mahsulot nomi (Rama, Balka...)"
												value={item.product_name}
												onChange={e => updateItem(idx, 'product_name', e.target.value)} />
											<input type="number" className="form-control" placeholder="Soni" min="0" step="0.001"
												style={{ maxWidth: '90px' }}
												value={item.quantity}
												onChange={e => updateItem(idx, 'quantity', e.target.value)} />
											<button type="button" className="btn btn-outline-danger px-2"
												onClick={() => removeItem(idx)}>
												<i className="uil-times"></i>
											</button>
										</div>
									))}
									<button type="button" className="btn btn-outline-secondary btn-sm"
										onClick={addItem}>
										<i className="uil-plus me-1"></i>Mahsulot qo'shish
									</button>
								</div>

								<div className="form-group">
									<label>Izoh</label>
									<textarea className="form-control" rows="2"
										value={form.note}
										onChange={e => setForm({ ...form, note: e.target.value })} />
								</div>

								<button type="submit" className="btn btn-primary w-100 mt-2">
									<i className="uil-check me-1"></i>Saqlash
								</button>
							</form>
						</div>
					</div>
				</div>

				{/* LOGS HISTORY */}
				<div className="col-md-7">
					<div className="card">
						<div className="card-body">
							<h6 className="card-title fw-bold mb-3">Mening yozuvlarim</h6>
							{logs.length === 0 ? (
								<div className="text-center text-muted py-4">Hozircha yozuvlar yo'q</div>
							) : (
								<div className="table-responsive">
									<table className="table table-bordered mb-0" style={{ fontSize: '13px' }}>
										<thead>
											<tr>
												<th>Sana</th>
												<th>Bo'lim</th>
												<th className="text-center">Ishchilar</th>
												<th className="text-end">Kraska (kg)</th>
												<th>Mahsulotlar</th>
												<th style={{ width: 40 }}></th>
											</tr>
										</thead>
										<tbody>
											{logs.map((log, idx) => (
												<tr key={idx}>
													<td className="text-nowrap">{formatDate(log.work_date, 'dd.MM.yyyy')}</td>
													<td>{log.product_type_name}</td>
													<td className="text-center fw-bold">{log.workers_count ?? '—'}</td>
														<td className="text-end fw-bold">{Number(log.paint_kg).toFixed(2)}</td>
													<td>
														{log.items && log.items.length > 0 ? (
															log.items.map((item, i) => (
																<span key={i} className="badge bg-light text-dark border me-1 mb-1">
																	{item.product_name}: <b>{item.quantity}</b>
																</span>
															))
														) : (
															<span className="text-muted">—</span>
														)}
														{log.note && (
															<div className="text-muted mt-1" style={{ fontSize: '11px' }}>{log.note}</div>
														)}
													</td>
													<td className="text-center">
														<button className="btn btn-sm btn-outline-danger p-1"
															style={{ lineHeight: 1 }}
															onClick={() => deleteLog(log.id)}>
															<i className="uil-trash-alt"></i>
														</button>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	)
}
